import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import {
    BadResponseWithError,
    FinalizedPermissionsResponse,
    Game,
} from '../../models/common.interface';
import { CommonService } from '../../services/common.service';
import { NotificationsService } from '../../services/notifications.service';
import { AdminCommonActions } from '../action/actions-type.action';

@Injectable({ providedIn: 'root' })
export class CommonEffect {
    downloadFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdminCommonActions.downloadFile),
            switchMap(({ payload }) =>
                this.commonService.downloadFile(payload.url).pipe(
                    map((blob: Blob) => {
                        let url = window.URL.createObjectURL(blob);
                        let a = document.createElement('a');
                        document.body.appendChild(a);
                        a.setAttribute('style', 'display: none');
                        a.href = url;
                        a.download = payload.fileName;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();

                        this.notificationService.showSuccessMessage(
                            'File generated.'
                        );

                        return AdminCommonActions.downloadFileSuccess();
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error?.message
                        );

                        return of(
                            AdminCommonActions.downloadFileFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    removeFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdminCommonActions.removeImageFile),
            switchMap(({ payload }) =>
                this.commonService.removeImageFile(payload.request).pipe(
                    map(() => {
                        this.notificationService.showSuccessMessage(
                            'Image file removed'
                        );

                        return AdminCommonActions.removeImageFileSuccess();
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationService.showError(
                            response.error?.message
                        );

                        return of(
                            AdminCommonActions.removeImageFileFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchGames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdminCommonActions.fetchPlatformGames),
            switchMap(() =>
                this.commonService.fetchGamesList().pipe(
                    map((response: Game[]) =>
                        AdminCommonActions.fetchPlatformGamesSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: BadResponseWithError) => {
                        return of(
                            AdminCommonActions.fetchPlatformGamesFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchPlayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdminCommonActions.fetchPlatformPlayers),
            switchMap(() =>
                this.commonService.fetchPlayersList().pipe(
                    map((response: any) =>
                        AdminCommonActions.fetchPlatformPlayersSuccess({
                            payload: response,
                        })
                    ),
                    catchError((response: BadResponseWithError) => {
                        return of(
                            AdminCommonActions.fetchPlatformPlayersFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    fetchPermissionsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AdminCommonActions.fetchPermissionsRequests),
            switchMap((action) => {
                return this.commonService
                    .fetchPermissionsRequest(action.payload)
                    .pipe(
                        map((response: FinalizedPermissionsResponse) => {
                            return AdminCommonActions.fetchPermissionsRequestsSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: any) => {
                            this.notificationService.showError(
                                response.message
                            );

                            return of(
                                AdminCommonActions.fetchPermissionsRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private commonService: CommonService,
        private notificationService: NotificationsService
    ) {}
}
